import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, Button, List, ListItem, Divider, Card, CardContent, Container } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuthService } from '../services/authService';
import { getCurrentUser, fetchAuthSession } from 'aws-amplify/auth';
import { Amplify } from 'aws-amplify';
import authConfig from '../config/auth.config';

// Determine if we're running locally
const IS_LOCALHOST = window.location.hostname === 'localhost' || 
                     window.location.hostname === '127.0.0.1' ||
                     window.location.hostname.includes('192.168.');

// Special function to re-initialize from cookies
const forceAuthFromCookies = async () => {
  console.log('Force initializing auth from cookies...');
  try {
    // Clear problematic undefined cookies first
    const domain = IS_LOCALHOST ? 'localhost' : '.screamify.com';
    document.cookie = `CognitoIdentityServiceProvider.undefined.LastAuthUser=; domain=${domain}; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    
    const allCookies = document.cookie;
    const cookiesArray = allCookies.split('; ');
    
    // Find and clear any cookies with undefined client ID
    const undefinedCookies = cookiesArray.filter(c => c.includes('CognitoIdentityServiceProvider.undefined'));
    
    if (undefinedCookies.length > 0) {
      console.log(`Cleaning up ${undefinedCookies.length} cookies with undefined client ID`);
      
      for (const cookie of undefinedCookies) {
        const cookieName = cookie.split('=')[0];
        document.cookie = `${cookieName}=; domain=${domain}; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
        console.log(`Cleared problematic cookie: ${cookieName}`);
      }
    }
    
    // Get the client ID from our config
    const clientId = authConfig.Auth.userPoolId;
    const webClientId = authConfig.Auth.userPoolWebClientId;
    const amplifyPrefix = `CognitoIdentityServiceProvider.${webClientId}`;
    
    // Configure Amplify with the proper v6 structure first
    const amplifyConfig = {
      Auth: {
        Cognito: {
          userPoolId: clientId,
          userPoolClientId: webClientId,
          loginWith: {
            email: true,
            phone: true,
            username: true
          }
        }
      },
      region: authConfig.Auth.region,
      cookieStorage: {
        domain: domain,
        path: '/',
        expires: 365,
        secure: !IS_LOCALHOST,
        sameSite: IS_LOCALHOST ? 'lax' : 'none'
      },
      oauth: authConfig.Auth.oauth
    };
    
    // Configure Amplify with the proper v6 structure
    Amplify.configure(amplifyConfig);
    console.log('Amplify configuration set with correct client ID and userPoolId');
    
    // Look for cookies in both formats
    const cookieCheckResult = checkForAuthCookies();
    
    // Try to get a session directly from cookies with additional options
    const session = await fetchAuthSession({
      forceRefresh: true,  // Force refresh tokens
      bypassCache: true    // Bypass any cached session
    });
    
    console.log('Force auth session result:', session);
    
    if (session?.tokens) {
      console.log('Successfully forced session from cookies');
      return session;
    } else {
      console.log('No tokens in forced session');
      
      // Get fresh cookies to display token info
      const refreshedCookies = document.cookie;
      const refreshedCookiesArray = refreshedCookies.split('; ');
      
      // Check for specific tokens
      const idTokenCookie = refreshedCookiesArray.find(c => c.startsWith('CognitoIdToken='));
      const accessTokenCookie = refreshedCookiesArray.find(c => c.startsWith('CognitoAccessToken='));
      const refreshTokenCookie = refreshedCookiesArray.find(c => c.startsWith('CognitoRefreshToken='));
      
      // Check for LastAuthUser in either format
      const lastAuthUserCookie = refreshedCookiesArray.find(c => 
        c.startsWith('LastAuthUser=') || c.startsWith(`${amplifyPrefix}.LastAuthUser=`)
      );
      let userId = null;
      
      if (lastAuthUserCookie) {
        userId = lastAuthUserCookie.split('=')[1];
        console.log('Found LastAuthUser cookie with user ID:', userId);
        
        // Check for Amplify formatted cookies with this user
        if (userId) {
          const userPrefix = `${amplifyPrefix}.${userId}`;
          const amplifyIdTokenCookie = refreshedCookiesArray.find(c => c.startsWith(`${userPrefix}.idToken=`));
          const amplifyAccessTokenCookie = refreshedCookiesArray.find(c => c.startsWith(`${userPrefix}.accessToken=`));
          
          if (amplifyIdTokenCookie || amplifyAccessTokenCookie) {
            console.log('Found standard Amplify-formatted cookies for user:', userId);
            
            // Format matches Amplify's expected pattern
            const idToken = amplifyIdTokenCookie ? amplifyIdTokenCookie.split('=')[1] : null;
            const accessToken = amplifyAccessTokenCookie ? amplifyAccessTokenCookie.split('=')[1] : null;
            
            // Try direct initialization with these tokens
            if (idToken && accessToken) {
              const tokenValidation = validateIdToken(idToken);
              console.log('Amplify token validation result:', tokenValidation);
              
              if (tokenValidation.valid) {
                console.log('Amplify token is valid, attempting initialization');
                try {
                  // Call handleForceAuth directly which handles all token validation
                  const result = await handleForceAuth();
                  if (result) {
                    console.log('Successfully handled force auth with Amplify tokens');
                    return { manualAuth: true, amplifyFormat: true };
                  }
                } catch (initError) {
                  console.error('Error in token initialization:', initError);
                }
              }
            }
          }
        }
      }
      
      if (idTokenCookie || accessTokenCookie) {
        console.log('Found custom-format token cookies:', {
          hasIdToken: !!idTokenCookie,
          hasAccessToken: !!accessTokenCookie,
          hasRefreshToken: !!refreshTokenCookie
        });
        
        // Try converting to Amplify format
        const idToken = idTokenCookie ? idTokenCookie.split('=')[1] : null;
        const accessToken = accessTokenCookie ? accessTokenCookie.split('=')[1] : null;
        
        if (idToken && accessToken) {
          // Extract user ID from the token
          const decodedUserId = extractUserIdFromToken(idToken);
          
          if (decodedUserId) {
            console.log('Extracted user ID from token:', decodedUserId);
            
            // Set cookies in Amplify format
            setAmplifyFormatCookies(webClientId, decodedUserId, idToken, accessToken, refreshTokenCookie ? refreshTokenCookie.split('=')[1] : null);
            
            // Try handleForceAuth instead of fetchAuthSession since it's more robust
            try {
              const result = await handleForceAuth();
              if (result) {
                console.log('Successfully handled force auth after converting cookies');
                return { manualAuth: true };
              }
            } catch (retryError) {
              console.log('Retry with converted cookies failed:', retryError.message);
            }
          }
        }
        
        // Try handleForceAuth as final alternative
        try {
          console.log('Attempting handleForceAuth as alternative...');
          const result = await handleForceAuth();
          if (result) {
            console.log('Successfully handled force auth as alternative');
            return { manualAuth: true };
          }
        } catch (authError) {
          console.log('handleForceAuth failed:', authError.message);
        }
      } else {
        console.log('No token cookies found in document.cookie');
      }
      
      return null;
    }
  } catch (error) {
    console.error('Error forcing auth from cookies:', error);
    return null;
  }
};

// Helper function to validate an ID token
const validateIdToken = (idToken) => {
  if (!idToken) return { valid: false, reason: 'No token provided' };
  
  try {
    // Tokens are in format header.payload.signature
    const parts = idToken.split('.');
    if (parts.length !== 3) {
      return { valid: false, reason: 'Invalid token format' };
    }
    
    // Try to decode the payload
    const payload = JSON.parse(atob(parts[1]));
    
    // Check expiration
    const now = Math.floor(Date.now() / 1000);
    if (payload.exp && payload.exp < now) {
      return { valid: false, reason: 'Token expired', expiry: new Date(payload.exp * 1000).toISOString() };
    }
    
    return { 
      valid: true, 
      tokenUse: payload.token_use,
      subject: payload.sub,
      issuer: payload.iss,
      audience: payload.aud,
      expiry: payload.exp ? new Date(payload.exp * 1000).toISOString() : 'unknown',
      username: payload['cognito:username'] || payload.username,
      userId: payload.sub // This is important
    };
  } catch (error) {
    return { valid: false, reason: `Failed to decode token: ${error.message}` };
  }
};

// Extract user ID from a token
const extractUserIdFromToken = (idToken) => {
  try {
    const parts = idToken.split('.');
    if (parts.length === 3) {
      const payload = JSON.parse(atob(parts[1]));
      return payload.sub || payload['cognito:username'] || null;
    }
    return null;
  } catch (e) {
    console.error('Error extracting user ID from token:', e);
    return null;
  }
};

// Set cookies in Amplify format
const setAmplifyFormatCookies = (clientId, userId, idToken, accessToken, refreshToken) => {
  try {
    const maxAge = 60 * 60 * 24 * 365; // 1 year in seconds
    const amplifyPrefix = `CognitoIdentityServiceProvider.${clientId}`;
    const userPrefix = `${amplifyPrefix}.${userId}`;
    const domain = IS_LOCALHOST ? 'localhost' : '.screamify.com';
    
    // Set LastAuthUser cookie (this is what Amplify checks first)
    document.cookie = `${amplifyPrefix}.LastAuthUser=${userId}; domain=${domain}; path=/; max-age=${maxAge}${!IS_LOCALHOST ? '; secure' : ''}; samesite=${IS_LOCALHOST ? 'lax' : 'none'}`;
    
    // Set ID token cookie
    if (idToken) {
      document.cookie = `${userPrefix}.idToken=${idToken}; domain=${domain}; path=/; max-age=${maxAge}${!IS_LOCALHOST ? '; secure' : ''}; samesite=${IS_LOCALHOST ? 'lax' : 'none'}`;
    }
    
    // Set access token cookie
    if (accessToken) {
      document.cookie = `${userPrefix}.accessToken=${accessToken}; domain=${domain}; path=/; max-age=${maxAge}${!IS_LOCALHOST ? '; secure' : ''}; samesite=${IS_LOCALHOST ? 'lax' : 'none'}`;
    }
    
    // Set refresh token cookie if available
    if (refreshToken) {
      document.cookie = `${userPrefix}.refreshToken=${refreshToken}; domain=${domain}; path=/; max-age=${maxAge}${!IS_LOCALHOST ? '; secure' : ''}; samesite=${IS_LOCALHOST ? 'lax' : 'none'}`;
    }
    
    console.log('Set cookies in standard Amplify format');
  } catch (e) {
    console.error('Error setting Amplify format cookies:', e);
  }
};

// Function to check token validity
const checkTokenValidity = (token) => {
  if (!token) return { valid: false, reason: 'No token provided' };
  
  try {
    // Tokens are in format header.payload.signature
    const parts = token.split('.');
    if (parts.length !== 3) {
      return { valid: false, reason: 'Invalid token format' };
    }
    
    // Try to decode the payload
    const payload = JSON.parse(atob(parts[1]));
    
    // Check expiration
    const now = Math.floor(Date.now() / 1000);
    if (payload.exp && payload.exp < now) {
      return { valid: false, reason: 'Token expired', expiry: new Date(payload.exp * 1000).toISOString() };
    }
    
    // Check for important fields
    const issuer = payload.iss;
    const tokenUse = payload.token_use;
    const audience = payload.aud;
    
    return { 
      valid: true, 
      tokenUse,
      subject: payload.sub,
      issuer,
      audience,
      expiry: payload.exp ? new Date(payload.exp * 1000).toISOString() : 'unknown',
      username: payload['cognito:username'] || payload.username
    };
  } catch (error) {
    return { valid: false, reason: `Failed to decode token: ${error.message}` };
  }
};

// Add direct token handling capability
const initializeWithTokensDirectly = async (idToken, accessToken) => {
  if (!idToken || !accessToken) return false;
  
  try {
    console.log('Initializing with direct token objects...');
    
    // Configure Amplify with the proper v6 structure
    const amplifyConfig = {
      Auth: {
        Cognito: {
          userPoolId: authConfig.Auth.userPoolId,
          userPoolClientId: authConfig.Auth.userPoolWebClientId,
          loginWith: {
            email: true,
            phone: false,
            username: false
          }
        }
      },
      region: authConfig.Auth.region,
      cookieStorage: authConfig.Auth.cookieStorage,
      oauth: authConfig.Auth.oauth
    };
    
    // Set the configuration
    Amplify.configure(amplifyConfig);
    console.log('Amplify reconfigured for direct token handling');
    
    // Attempt to create a session
    const session = await fetchAuthSession({ 
      forceRefresh: true,
      bypassCache: true 
    });
    
    console.log('Fetch session result:', session);
    
    if (session && session.tokens) {
      console.log('Successfully created session from tokens');
      return true;
    }
    
    console.log('Session created but no tokens returned');
    return false;
  } catch (error) {
    console.error('Error in direct token initialization:', error);
    return false;
  }
}

// Helper function to check auth cookies
const checkForAuthCookies = () => {
  const allCookies = document.cookie;
  const cookiesArray = allCookies.split('; ');
  
  // The client ID from our config is needed to detect standard Amplify cookie names
  const clientId = authConfig.Auth.userPoolWebClientId;
  const amplifyPrefix = `CognitoIdentityServiceProvider.${clientId}`;
  
  // Look for both standard and custom cookies
  const cognitoCookies = cookiesArray.filter(cookie => 
    cookie.startsWith('CognitoIdentityServiceProvider') || 
    cookie.includes(`${amplifyPrefix}.LastAuthUser`) ||
    cookie.includes('.idToken') ||
    cookie.includes('.accessToken') ||
    cookie.includes('.refreshToken') ||
    cookie.includes('LastAuthUser') ||
    cookie.includes('CognitoIdToken') ||
    cookie.includes('CognitoAccessToken') ||
    cookie.includes('CognitoRefreshToken') ||
    cookie.includes('ScreamifyAuth') || 
    cookie.includes('testCookie')
  );
  
  return {
    cookies: cognitoCookies,
    hasCookies: cognitoCookies.length > 0
  };
};

const CookieTest = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user, getToken, getUserAttributes, checkAuthState, clearExistingAuth } = useAuthService();
  const [authStatus, setAuthStatus] = useState({
    isLoggedIn: false,
    note: 'Checking authentication status...',
    manualTokenSuccess: false,
    cookies: [],
    localStorage: []
  });
  const [userInfo, setUserInfo] = useState(null);
  const [token, setToken] = useState('');
  const [forcedAuth, setForcedAuth] = useState(false);
  
  // Domain information for debugging
  const domain = {
    current: window.location.hostname,
    href: window.location.href,
    isLocalhost: IS_LOCALHOST,
    cookieDomain: IS_LOCALHOST ? 'localhost' : '.screamify.com'
  };
  
  // Check for cookies and authentication on mount
  useEffect(() => {
    const initializeAuth = async () => {
      console.log("Initializing authentication on page load...");
      
      // Clear any problematic undefined cookies first
      const domain = IS_LOCALHOST ? 'localhost' : '.screamify.com';
      document.cookie = `CognitoIdentityServiceProvider.undefined.LastAuthUser=; domain=${domain}; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
      
      // Find and clear any cookies with undefined client ID
      const allCookies = document.cookie;
      const cookiesArray = allCookies.split('; ');
      const undefinedCookies = cookiesArray.filter(c => c.includes('CognitoIdentityServiceProvider.undefined'));
      
      if (undefinedCookies.length > 0) {
        console.log(`Cleaning up ${undefinedCookies.length} cookies with undefined client ID`);
        
        for (const cookie of undefinedCookies) {
          const cookieName = cookie.split('=')[0];
          document.cookie = `${cookieName}=; domain=${domain}; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
        }
      }
      
      // Wait a moment for cookies to settle
      await new Promise(resolve => setTimeout(resolve, 200));
      
      // First, try the standard check
      await checkAuth();
      
      // If not authenticated but we have cookies, forcibly try to initialize
      const { cookies } = checkForAuthCookies();
      const hasAmplifyFormatCookies = cookies.some(cookie => 
        cookie.includes(`CognitoIdentityServiceProvider.${authConfig.Auth.userPoolWebClientId}`)
      );
      
      if (!authStatus.isLoggedIn && hasAmplifyFormatCookies) {
        console.log("Found Amplify cookies but not authenticated, trying force initialization...");
        
        // Configure Amplify with the latest settings
        const amplifyConfig = {
          Auth: {
            Cognito: {
              userPoolId: authConfig.Auth.userPoolId,
              userPoolClientId: authConfig.Auth.userPoolWebClientId,
              loginWith: {
                email: true,
                phone: true,
                username: true
              }
            }
          },
          region: authConfig.Auth.region,
          cookieStorage: authConfig.Auth.cookieStorage,
          oauth: authConfig.Auth.oauth
        };
        
        // Apply configuration
        Amplify.configure(amplifyConfig);
        
        try {
          // Try using our force auth method directly
          const success = await handleForceAuth();
          
          if (success) {
            console.log("Force initialization successful!");
          } else {
            console.log("Force initialization did not succeed. Try clicking the Force Auth button manually.");
          }
        } catch (error) {
          console.error("Error during force initialization:", error);
        }
      }
    };
    
    // Run initialization with a slight delay to allow all components to mount
    setTimeout(() => {
      initializeAuth();
    }, 300);
  }, []);
  
  // Function to detect auth cookies
  const checkAuth = async (force = false) => {
    console.log("Testing authentication cookies...");
    
    // Get all cookies
    const allCookies = document.cookie.split(';').map(cookie => cookie.trim());
    
    // Get config for Amplify cookie format
    const clientId = authConfig.Auth.userPoolWebClientId;
    const amplifyPrefix = `CognitoIdentityServiceProvider.${clientId}`;
    
    // Find LastAuthUser in Amplify format
    const lastAuthUserCookie = allCookies.find(cookie => 
      cookie.startsWith(`${amplifyPrefix}.LastAuthUser=`)
    );
    
    let userId = null;
    if (lastAuthUserCookie) {
      userId = lastAuthUserCookie.split('=')[1];
      console.log("Found LastAuthUser cookie with user ID:", userId);
    }
    
    // Filter for auth cookies
    const authCookies = allCookies.filter(cookie => 
      cookie.startsWith('CognitoIdentityServiceProvider') || 
      cookie.includes('LastAuthUser') ||
      cookie.includes('CognitoIdToken') ||
      cookie.includes('CognitoAccessToken') ||
      cookie.includes('CognitoRefreshToken') ||
      cookie.includes('ScreamifyAuth') ||
      cookie.includes('ScreamifyAuthBackup') ||
      cookie.includes('testCookie')
    );
    
    // Check for Amplify-format cookies
    const hasAmplifyFormatCookies = userId && allCookies.some(cookie => 
      cookie.startsWith(`${amplifyPrefix}.${userId}.idToken`) || 
      cookie.startsWith(`${amplifyPrefix}.${userId}.accessToken`)
    );
    
    console.log("Amplify-format cookies found:", hasAmplifyFormatCookies);
    
    // Check localStorage for auth items in dev environment
    const localStorageAuth = [];
    try {
      // Get all localStorage keys
      const keys = Object.keys(localStorage);
      
      // Filter auth-related localStorage items
      for (const key of keys) {
        if (
          key.includes('CognitoIdentityServiceProvider') ||
          key.includes('screamify_auth') ||
          key.includes('screamify_login') ||
          key.includes('screamify_cross_domain_auth')
        ) {
          // Only show key name, not the value for security
          localStorageAuth.push(key);
        }
      }
    } catch (e) {
      console.error("Error checking localStorage:", e);
    }
    
    // Check if we have auth
    const hasAuthCookies = authCookies.length > 0;
    const hasLocalStorageAuth = localStorageAuth.length > 0;
    
    console.log("Auth cookies exist:", hasAuthCookies);
    console.log("Auth cookies:", authCookies);
    
    // If we have Amplify-format cookies but we're not authenticated,
    // try to convert them to tokens and force authentication
    if (hasAmplifyFormatCookies && (force || !isAuthenticated)) {
      console.log("Found Amplify-format cookies. Attempting to use them directly...");
      
      // Configure Amplify with proper settings for this domain
      const amplifyConfig = {
        Auth: {
          Cognito: {
            userPoolId: authConfig.Auth.userPoolId,
            userPoolClientId: clientId,
            loginWith: {
              email: true,
              phone: true,
              username: true
            }
          }
        },
        region: authConfig.Auth.region,
        cookieStorage: {
          domain: IS_LOCALHOST ? 'localhost' : '.screamify.com',
          path: '/',
          expires: 365,
          secure: !IS_LOCALHOST,
          sameSite: IS_LOCALHOST ? 'lax' : 'none'
        },
        oauth: authConfig.Auth.oauth
      };
      
      // Apply the configuration
      Amplify.configure(amplifyConfig);
      console.log("Reconfigured Amplify with proper settings");
      
      try {
        // Try to get a session directly
        const session = await fetchAuthSession({
          forceRefresh: true,
          bypassCache: true
        });
        
        if (session?.tokens) {
          console.log("Successfully retrieved session from Amplify-format cookies!");
          
          // Update UI right away
          try {
            const currentUser = await getCurrentUser();
            const attributes = currentUser ? await getUserAttributes() : {};
            
            setUserInfo({
              username: currentUser?.username || userId,
              email: attributes?.email || "Retrieved from cookies",
              displayName: attributes?.['custom:displayName'] || "Authenticated User"
            });
            
            if (session.tokens.idToken) {
              const safeToken = session.tokens.idToken.toString().substring(0, 10) + '...[truncated]';
              setToken(safeToken);
            }
            
            setAuthStatus({
              isLoggedIn: true,
              cookies: authCookies,
              localStorage: localStorageAuth,
              note: "Successfully authenticated with Amplify cookies"
            });
            
            return; // Exit early since we succeeded
          } catch (userError) {
            console.error("Error getting user details after session retrieval:", userError);
          }
        }
      } catch (sessionError) {
        console.error("Error getting session from Amplify cookies:", sessionError);
      }
    }
    
    // Use auth service to verify authentication
    try {
      await checkAuthState();
      
      console.log("After checkAuthState, isAuthenticated:", isAuthenticated);
      
      // Force auth service to check auth cookies even if state doesn't reflect it
      // This is a more direct check that doesn't rely on the cached state
      if (hasAuthCookies && !isAuthenticated) {
        console.log("Cookies present but isAuthenticated is false. Performing direct token check...");
        
        try {
          // Try to get token directly
          const tokenResult = await getToken();
          console.log("Token retrieval result:", tokenResult ? "Success" : "Failed");
          
          if (tokenResult) {
            console.log("Token was retrieved successfully despite isAuthenticated=false");
            // We have a valid token! Update UI to reflect this
            // even if the global state hasn't caught up yet
            
            // Get user information if token is valid
            try {
              const currentUser = await getCurrentUser();
              if (currentUser) {
                console.log("Current user retrieved directly:", currentUser);
                
                // Set user info from directly retrieved data
                setUserInfo({
                  username: currentUser.username,
                  email: "Token valid but email not loaded",
                  displayName: "Token valid but displayName not loaded"
                });
                
                // Set token preview
                if (tokenResult) {
                  const safeToken = tokenResult.substring(0, 10) + '...[truncated]';
                  setToken(safeToken);
                }
                
                // Update auth status - we know we're authenticated even if global state doesn't reflect it yet
                setAuthStatus({
                  isLoggedIn: true,
                  cookies: authCookies,
                  localStorage: localStorageAuth,
                  note: "Auth confirmed via direct token check"
                });
                
                return; // Exit early as we've confirmed auth
              }
            } catch (userError) {
              console.error("Error getting current user:", userError);
              // Continue with normal flow
            }
          }
        } catch (tokenError) {
          console.error("Direct token check failed:", tokenError);
          // If token check failed, cookies might be invalid, continue with normal flow
        }
      }
      
      // Get token and user info if authenticated
      if (isAuthenticated && user) {
        try {
          const tokenResult = await getToken();
          const attributes = await getUserAttributes();
          
          // Safely display token (first 10 chars)
          if (tokenResult) {
            const safeToken = tokenResult.substring(0, 10) + '...[truncated]';
            setToken(safeToken);
          }
          
          // Set user info
          setUserInfo({
            username: user.username,
            email: attributes?.email || 'Not available',
            displayName: attributes?.['custom:displayName'] || 'Not available'
          });
        } catch (error) {
          console.error("Error getting token or attributes:", error);
        }
      } else {
        // Initialize userInfo with empty values if not authenticated
        setUserInfo({
          username: null,
          email: null,
          displayName: null
        });
      }
    } catch (authCheckError) {
      console.error("Error checking auth state:", authCheckError);
      // Initialize userInfo with empty values on error
      setUserInfo({
        username: null,
        email: null,
        displayName: null
      });
    }
    
    // Update state - always include cookies and localStorage arrays
    setAuthStatus({
      isLoggedIn: isAuthenticated,
      cookies: authCookies || [],
      localStorage: localStorageAuth || [],
      note: hasAmplifyFormatCookies && !isAuthenticated 
        ? "Found Amplify cookies but failed to authenticate. Try clicking Force Auth."
        : null,
      manualTokenSuccess: authStatus.manualTokenSuccess
    });
  };
  
  // Function to clear all auth cookies and sign out
  const handleLogout = async () => {
    console.log('Attempting to log out and clear all cookies...');
    
    setAuthStatus(prev => ({
      ...prev,
      note: 'Logging out...'
    }));
    
    try {
      // First, try the standard Amplify signOut
      await signOut();
      console.log('Amplify signOut completed');
      
      // Then, ensure we clear all possible cookie formats
      const clearCookieFormats = () => {
        console.log('Clearing all cookie formats...');
        
        // Get all cookies
        const allCookies = document.cookie;
        const cookiesArray = allCookies.split('; ');
        
        // Find all auth-related cookies
        const clientId = authConfig.Auth.userPoolWebClientId;
        const amplifyPrefix = `CognitoIdentityServiceProvider.${clientId}`;
        
        // Find all auth-related cookies including LastAuthUser
        const authCookieNames = [];
        
        cookiesArray.forEach(cookie => {
          const cookieName = cookie.split('=')[0];
          
          // Check for Amplify-format cookies
          if (cookieName.startsWith('CognitoIdentityServiceProvider')) {
            authCookieNames.push(cookieName);
          }
          
          // Check for our custom format cookies
          if (cookieName === 'CognitoIdToken' || 
              cookieName === 'CognitoAccessToken' || 
              cookieName === 'CognitoRefreshToken' || 
              cookieName === 'LastAuthUser' ||
              cookieName === 'ScreamifyAuth' ||
              cookieName === 'testCookie') {
            authCookieNames.push(cookieName);
          }
        });
        
        // If we found LastAuthUser, look for user-specific cookies
        const lastAuthUserCookie = cookiesArray.find(cookie => 
          cookie.startsWith(`${amplifyPrefix}.LastAuthUser=`) || 
          cookie.startsWith('LastAuthUser=')
        );
        
        if (lastAuthUserCookie) {
          const userId = lastAuthUserCookie.split('=')[1];
          console.log('Found LastAuthUser with ID:', userId);
          
          // Look for cookies with this user ID
          cookiesArray.forEach(cookie => {
            const cookieName = cookie.split('=')[0];
            if (cookieName.includes(userId)) {
              authCookieNames.push(cookieName);
            }
          });
        }
        
        console.log('Found auth cookies to clear:', authCookieNames);
        
        // Clear all found auth cookies on all possible domains
        const domains = [
          '',                   // Current domain
          document.domain,      // Document domain
          `.${document.domain}`, // Dot-prefixed document domain
          'localhost',          // For local development
          '.localhost',         // For local development with subdomains
          'screamify.com',      // Main domain
          '.screamify.com',     // For all subdomains
          'www.screamify.com',  // WWW subdomain
          'shop.screamify.com'  // Shop subdomain
        ];
        
        const paths = ['/', '/store', '/shop'];
        
        // Clear each cookie on all domains and paths
        domains.forEach(domain => {
          paths.forEach(path => {
            authCookieNames.forEach(cookieName => {
              const cookieString = `${cookieName}=; path=${path}; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
              const cookieWithDomain = domain ? `${cookieString}; domain=${domain}` : cookieString;
              document.cookie = cookieWithDomain;
              console.log(`Cleared cookie: ${cookieName} on domain: ${domain || 'current'} path: ${path}`);
            });
          });
        });
        
        // Extra safety - clear all Cognito cookies without specifying names
        domains.forEach(domain => {
          paths.forEach(path => {
            const domainString = domain ? `; domain=${domain}` : '';
            document.cookie = `CognitoIdentityServiceProvider*=; path=${path}${domainString}; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
            document.cookie = `CognitoIdToken=; path=${path}${domainString}; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
            document.cookie = `CognitoAccessToken=; path=${path}${domainString}; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
            document.cookie = `CognitoRefreshToken=; path=${path}${domainString}; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
            document.cookie = `LastAuthUser=; path=${path}${domainString}; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
          });
        });
      };
      
      // Clear all cookies
      clearCookieFormats();
      
      // Reset state
      setAuthStatus({
        isLoggedIn: false,
        note: 'Successfully logged out',
        manualTokenSuccess: false,
        cookies: [],
        localStorage: []
      });
      setUserInfo(null);
      setToken('');
      setForcedAuth(false);
      
      // Refresh page after a short delay
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      console.error('Error during logout:', error);
      setAuthStatus(prev => ({
        ...prev,
        note: `Logout error: ${error.message}`
      }));
    }
  };
  
  // Function to force auth from cookies
  const handleForceAuth = async () => {
    console.log('Attempting to force auth from cookies...');
    
    // Show that we're working on it
    setAuthStatus(prev => ({
      ...prev,
      note: 'Attempting to force authentication from cookies...'
    }));
    
    try {
      // Clear problematic undefined cookies first
      const domain = IS_LOCALHOST ? 'localhost' : '.screamify.com';
      document.cookie = `CognitoIdentityServiceProvider.undefined.LastAuthUser=; domain=${domain}; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
      
      // Find and clear any cookies with undefined client ID
      const allCookies = document.cookie;
      const cookiesArray = allCookies.split('; ');
      const undefinedCookies = cookiesArray.filter(c => c.includes('CognitoIdentityServiceProvider.undefined'));
      
      if (undefinedCookies.length > 0) {
        console.log(`Cleaning up ${undefinedCookies.length} cookies with undefined client ID`);
        
        for (const cookie of undefinedCookies) {
          const cookieName = cookie.split('=')[0];
          document.cookie = `${cookieName}=; domain=${domain}; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
        }
      }
      
      // Extract all relevant cookies after clearing problematic ones
      const refreshedCookies = document.cookie;
      const refreshedCookiesArray = refreshedCookies.split('; ');
      
      // 1. First, try the direct token extraction approach (most reliable)
      let idToken = null;
      let accessToken = null;
      let refreshToken = null;
      let userId = null;
      
      // Direct format tokens
      const directIdToken = refreshedCookiesArray.find(c => c.startsWith('CognitoIdToken='));
      const directAccessToken = refreshedCookiesArray.find(c => c.startsWith('CognitoAccessToken='));
      
      if (directIdToken && directAccessToken) {
        idToken = directIdToken.split('=')[1];
        accessToken = directAccessToken.split('=')[1];
        
        // Extract user ID from the token
        try {
          const parts = idToken.split('.');
          if (parts.length === 3) {
            const payload = JSON.parse(atob(parts[1]));
            userId = payload.sub || payload['cognito:username'];
            console.log('Extracted user ID from token:', userId);
          }
        } catch (parseError) {
          console.error('Error parsing token to extract user ID:', parseError);
        }
      }
      
      // If we found tokens and a user ID
      if (idToken && accessToken && userId) {
        console.log('Found valid tokens and user ID, proceeding with manual authentication');
        
        // Create a minimal version of the tokens to use
        const idTokenPayload = parseJwt(idToken);
        const accessTokenPayload = parseJwt(accessToken);
        
        // Manually create a user object
        const manualUser = {
          username: userId,
          userId: userId,
          email: idTokenPayload.email || 'unknown',
          displayName: idTokenPayload['custom:displayName'] || 'Authenticated User'
        };
        
        // Determine token expiry
        const now = Math.floor(Date.now() / 1000);
        const idTokenExpiry = idTokenPayload.exp || (now + 3600);
        const accessTokenExpiry = accessTokenPayload.exp || (now + 3600);
        
        // Update UI to reflect authentication
        setForcedAuth(true);
        setUserInfo({
          username: manualUser.username,
          email: manualUser.email,
          displayName: manualUser.displayName
        });
        
        // Set token display
        const safeToken = idToken.substring(0, 10) + '...[truncated]';
        setToken(safeToken);
        
        // Update auth status
        setAuthStatus(prev => ({
          ...prev,
          isLoggedIn: true,
          manualTokenSuccess: true,
          note: 'Manually authenticated with direct token approach'
        }));
        
        // Ensure cookies are in the proper Amplify format with the correct client ID
        const clientId = authConfig.Auth.userPoolWebClientId;
        const amplifyPrefix = `CognitoIdentityServiceProvider.${clientId}`;
        const userPrefix = `${amplifyPrefix}.${userId}`;
        
        // Set LastAuthUser cookie (this is what Amplify checks first)
        document.cookie = `${amplifyPrefix}.LastAuthUser=${userId}; domain=${domain}; path=/; max-age=${60 * 60 * 24 * 365}${!IS_LOCALHOST ? '; secure' : ''}; samesite=${IS_LOCALHOST ? 'lax' : 'none'}`;
        
        // Set ID token cookie in Amplify format
        document.cookie = `${userPrefix}.idToken=${idToken}; domain=${domain}; path=/; max-age=${60 * 60 * 24 * 365}${!IS_LOCALHOST ? '; secure' : ''}; samesite=${IS_LOCALHOST ? 'lax' : 'none'}`;
        
        // Set access token cookie in Amplify format
        document.cookie = `${userPrefix}.accessToken=${accessToken}; domain=${domain}; path=/; max-age=${60 * 60 * 24 * 365}${!IS_LOCALHOST ? '; secure' : ''}; samesite=${IS_LOCALHOST ? 'lax' : 'none'}`;
        
        // Also set the userData cookie which helps some Amplify operations
        const userDataObj = {
          username: userId,
          email: manualUser.email,
          exp: new Date(idTokenExpiry * 1000).toISOString()
        };
        const userDataStr = JSON.stringify(userDataObj);
        document.cookie = `${amplifyPrefix}.userData=${encodeURIComponent(userDataStr)}; domain=${domain}; path=/; max-age=${60 * 60 * 24 * 365}${!IS_LOCALHOST ? '; secure' : ''}; samesite=${IS_LOCALHOST ? 'lax' : 'none'}`;
        
        // Create a backup in our own cookie format too
        document.cookie = `ScreamifyAuth=${new Date(idTokenExpiry * 1000).toISOString()}; domain=${domain}; path=/; max-age=${60 * 60 * 24 * 365}${!IS_LOCALHOST ? '; secure' : ''}; samesite=${IS_LOCALHOST ? 'lax' : 'none'}`;
        
        console.log('Authentication successful using direct token approach');
        console.log('Set cookies in Amplify format with correct client ID');
        
        // Try to initialize Amplify with the newly set cookies
        try {
          // Configure Amplify properly before trying to use the session
          const amplifyConfig = {
            Auth: {
              Cognito: {
                userPoolId: authConfig.Auth.userPoolId,
                userPoolClientId: clientId,
                loginWith: {
                  email: true,
                  phone: false,
                  username: false
                }
              }
            },
            region: authConfig.Auth.region,
            cookieStorage: {
              domain: domain,
              path: '/',
              expires: 365,
              secure: !IS_LOCALHOST,
              sameSite: IS_LOCALHOST ? 'lax' : 'none'
            },
            oauth: authConfig.Auth.oauth
          };
          
          // Set the configuration
          Amplify.configure(amplifyConfig);
          console.log('Reconfigured Amplify with proper settings');
          
          // Wait a second to allow cookies to settle
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          // Don't refresh the page - it can cause the auth state to reset
          // Just stay on the current page with our manual auth state
        } catch (ampError) {
          console.error('Error configuring Amplify after force auth:', ampError);
        }
        
        return true;
      }
      
      // 2. If approach #1 failed, try to use tokens from Amplify format cookies
      
      // Get the client ID from config
      const clientId = authConfig.Auth.userPoolWebClientId;
      const amplifyPrefix = `CognitoIdentityServiceProvider.${clientId}`;
      
      // Look for LastAuthUser in Amplify format
      const lastAuthUserCookie = refreshedCookiesArray.find(cookie => 
        cookie.startsWith(`${amplifyPrefix}.LastAuthUser=`)
      );
      
      if (lastAuthUserCookie) {
        userId = lastAuthUserCookie.split('=')[1];
        console.log('Found LastAuthUser cookie with user ID:', userId);
        
        // Look for tokens in Amplify format
        const userPrefix = `${amplifyPrefix}.${userId}`;
        const amplifyIdToken = refreshedCookiesArray.find(c => c.startsWith(`${userPrefix}.idToken=`));
        const amplifyAccessToken = refreshedCookiesArray.find(c => c.startsWith(`${userPrefix}.accessToken=`));
        
        if (amplifyIdToken && amplifyAccessToken) {
          idToken = amplifyIdToken.split('=')[1];
          accessToken = amplifyAccessToken.split('=')[1];
          
          console.log('Found valid tokens in Amplify format, proceeding with authentication');
          
          // Extract token payloads
          const idTokenPayload = parseJwt(idToken);
          
          // Update UI to reflect authentication
          setForcedAuth(true);
          setUserInfo({
            username: userId,
            email: idTokenPayload.email || 'Email from Amplify cookie',
            displayName: idTokenPayload['custom:displayName'] || 'Authenticated User'
          });
          
          // Set token display
          const safeToken = idToken.substring(0, 10) + '...[truncated]';
          setToken(safeToken);
          
          // Update auth status
          setAuthStatus(prev => ({
            ...prev,
            isLoggedIn: true,
            manualTokenSuccess: true,
            note: 'Authenticated with Amplify format cookies'
          }));
          
          console.log('Authentication successful using Amplify format cookies');
          
          // Try to configure Amplify to use these tokens
          const amplifyConfig = {
            Auth: {
              Cognito: {
                userPoolId: authConfig.Auth.userPoolId,
                userPoolClientId: clientId,
                loginWith: {
                  email: true,
                  phone: true,
                  username: true
                }
              }
            },
            region: authConfig.Auth.region,
            cookieStorage: {
              domain: domain,
              path: '/',
              expires: 365,
              secure: !IS_LOCALHOST,
              sameSite: IS_LOCALHOST ? 'lax' : 'none'
            }
          };
          
          // Apply configuration
          Amplify.configure(amplifyConfig);
          console.log('Reconfigured Amplify with proper settings for these cookies');
          
          // Wait a bit for cookies to settle
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          // Don't auto-refresh, just stay on the current page with manual auth state
          
          return true;
        }
      }
      
      // 3. If we still haven't succeeded, show token validation information
      if (directIdToken) {
        const token = directIdToken.split('=')[1];
        const idTokenValidity = checkTokenValidity(token);
        console.log('ID Token validation result:', idTokenValidity);
        
        // Display info about the token
        let tokenNote = '';
        if (!idTokenValidity.valid) {
          if (idTokenValidity.reason === 'Token expired') {
            const expiry = new Date(idTokenValidity.expiry);
            const now = new Date();
            const diffMs = now - expiry;
            const diffMins = Math.floor(diffMs / 60000);
            const diffHours = Math.floor(diffMins / 60);
            
            tokenNote = `ID Token expired ${diffHours} hours and ${diffMins % 60} minutes ago. Please log in again.`;
          } else {
            tokenNote = `ID Token invalid: ${idTokenValidity.reason}`;
          }
        } else {
          tokenNote = `ID Token is valid and expires on ${idTokenValidity.expiry}. 
            Unable to authenticate with valid token. Please try logging in again at www.screamify.com.`;
        }
        
        // Update UI with token info
        setUserInfo({
          username: idTokenValidity.username || userId || "Unknown",
          email: idTokenValidity.valid ? "Valid token but authentication failed. Try clearing cookies and logging in again." : "Invalid token",
          displayName: "Token extracted user"
        });
        
        const safeToken = token.substring(0, 10) + '...[truncated]';
        setToken(safeToken);
        
        setAuthStatus(prev => ({
          ...prev,
          manualTokenSuccess: idTokenValidity.valid,
          note: tokenNote
        }));
        
        return false;
      } else {
        setAuthStatus(prev => ({
          ...prev,
          note: 'No authentication cookies found. Please log in first at www.screamify.com.'
        }));
        return false;
      }
    } catch (error) {
      console.error('Error in force auth:', error);
      setAuthStatus(prev => ({
        ...prev,
        note: `Error: ${error.message}`
      }));
      return false;
    }
  };
  
  // Helper function to parse JWT token
  const parseJwt = (token) => {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Error parsing JWT:', error);
      return {};
    }
  };
  
  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3, borderRadius: 2, bgcolor: 'background.paper' }}>
        <Typography variant="h4" component="h1" gutterBottom color="primary" sx={{ fontWeight: 'bold' }}>
          Authentication Cookie Test
        </Typography>
        
        {/* Domain Info Display */}
        <Box sx={{ mb: 2, p: 2, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Domain Information:</Typography>
          <Typography variant="body2">Current domain: <strong>{domain.current}</strong></Typography>
          <Typography variant="body2">URL: <strong>{domain.href}</strong></Typography>
          <Typography variant="body2">
            Environment: <strong>{domain.isLocalhost ? 'Local Development' : 'Production'}</strong>
          </Typography>
          <Typography variant="body2">Cookie domain: <strong>{domain.cookieDomain}</strong></Typography>
        </Box>
        
        {/* Authentication Status */}
        <Box sx={{ mb: 3, p: 2, bgcolor: authStatus.isLoggedIn ? 'success.light' : 'warning.light', borderRadius: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: authStatus.isLoggedIn ? 'success.dark' : 'warning.dark' }}>
            Status: {authStatus.isLoggedIn ? 'Logged In' : 'Not Logged In'}
          </Typography>
          
          {authStatus.note && (
            <Typography variant="body2" sx={{ mt: 1, color: authStatus.isLoggedIn ? 'success.dark' : 'warning.dark' }}>
              {authStatus.note}
            </Typography>
          )}
        </Box>
        
        {userInfo && userInfo.username && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>User Information:</Typography>
              <Typography>Username: {userInfo.username}</Typography>
              <Typography>Email: {userInfo.email}</Typography>
              <Typography>Display Name: {userInfo.displayName}</Typography>
              {token && (
                <Typography sx={{ mt: 2 }}>
                  Token Preview: {token}
                </Typography>
              )}
            </CardContent>
          </Card>
        )}
        
        <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button variant="contained" color="primary" onClick={checkAuth}>
            Refresh Status
          </Button>
          
          <Button 
            variant="contained" 
            color="warning" 
            onClick={handleForceAuth}
          >
            Force Auth from Cookies
          </Button>
          
          {authStatus.manualTokenSuccess && !authStatus.isLoggedIn && (
            <Button 
              variant="contained" 
              color="secondary"
              onClick={async () => {
                // Extract tokens again
                const allCookies = document.cookie;
                const cookiesArray = allCookies.split('; ');
                
                let idToken = null;
                let accessToken = null;
                
                for (const cookie of cookiesArray) {
                  if (cookie.startsWith('CognitoIdToken=')) {
                    idToken = cookie.split('=')[1];
                  } else if (cookie.startsWith('CognitoAccessToken=')) {
                    accessToken = cookie.split('=')[1];
                  }
                }
                
                if (idToken && accessToken) {
                  // Try the direct initialization
                  const result = await initializeWithTokensDirectly(idToken, accessToken);
                  if (result) {
                    window.location.reload();
                  } else {
                    alert('Token repair failed. Try logging in again.');
                  }
                } else {
                  alert('No valid tokens found to repair.');
                }
              }}
            >
              Use Manual Auth
            </Button>
          )}
          
          {authStatus.isLoggedIn && (
            <Button variant="contained" color="error" onClick={handleLogout}>
              Logout
            </Button>
          )}
          
          {!authStatus.isLoggedIn && (
            <Button variant="contained" color="success" onClick={() => navigate('/login')}>
              Go to Login
            </Button>
          )}
          
          <Button variant="outlined" onClick={() => navigate('/')}>
            Back to Home
          </Button>
        </Box>
        
        {forcedAuth && (
          <Box sx={{ mt: 3, p: 2, backgroundColor: 'rgba(255, 152, 0, 0.1)', borderRadius: 2, border: '1px solid orange' }}>
            <Typography variant="subtitle1" color="warning.main" fontWeight="bold">
              Force Auth Status: Success
            </Typography>
            <Typography variant="body2">
              Authentication was forced using cookie values directly. Page will refresh automatically to update state.
            </Typography>
          </Box>
        )}
        
        {authStatus.manualTokenSuccess && !authStatus.isLoggedIn && (
          <Box sx={{ mt: 3, p: 2, backgroundColor: 'rgba(255, 235, 59, 0.1)', borderRadius: 2, border: '1px solid #ffeb3b', display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="subtitle1" fontWeight="bold">
              What to do if tokens are expired:
            </Typography>
            <Typography variant="body2">
              1. Go back to <a href="https://www.screamify.com/login" target="_blank" rel="noopener noreferrer">www.screamify.com/login</a> and login again
            </Typography>
            <Typography variant="body2">
              2. After login, immediately come back to this page (shop.screamify.com/cookie-test)
            </Typography>
            <Typography variant="body2">
              3. Click the "Force Auth from Cookies" button again
            </Typography>
            <Typography variant="body2">
              4. If tokens are still valid, authentication should work across domains
            </Typography>
          </Box>
        )}
      </Paper>
      
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Authentication Cookies Found: {(authStatus.cookies || []).length}
        </Typography>
        
        <List>
          {(authStatus.cookies || []).length > 0 ? (
            (authStatus.cookies || []).map((cookie, index) => {
              // Only show the cookie name, not the full value (for security)
              const cookieParts = cookie.split('=');
              const cookieName = cookieParts[0];
              const valuePreview = cookieParts.length > 1 ? 
                (cookieParts[1].length > 20 ? 
                  cookieParts[1].substring(0, 10) + '...[truncated]' : 
                  cookieParts[1]) : 
                '';
              
              return (
                <React.Fragment key={index}>
                  <ListItem>
                    <Typography>
                      <strong>{cookieName}</strong>: {valuePreview}
                    </Typography>
                  </ListItem>
                  {index < (authStatus.cookies || []).length - 1 && <Divider />}
                </React.Fragment>
              );
            })
          ) : (
            <ListItem>
              <Typography color="error.main">No auth cookies found</Typography>
            </ListItem>
          )}
        </List>
        
        {(authStatus.localStorage || []).length > 0 && (
          <>
            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              LocalStorage Auth Items Found: {(authStatus.localStorage || []).length}
            </Typography>
            
            <List>
              {(authStatus.localStorage || []).map((item, index) => (
                <React.Fragment key={index}>
                  <ListItem>
                    <Typography>
                      <strong>{item}</strong>
                    </Typography>
                  </ListItem>
                  {index < (authStatus.localStorage || []).length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </>
        )}
        
        <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={() => {
              console.log("Manual page refresh triggered");
              window.location.reload();
            }}
          >
            Reload Page
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default CookieTest; 